import {
    AccountApi,
    ClaimEditorStore,
    CoreFetch,
    ICustomFilter,
    ListProviderLettersRequest,
    ListProviderMailRequest,
    ListProviderReceiveFaxesRequest,
    ListProviderSendFaxesRequest,
    MeApi,
    MeInfoResponse,
    ProviderLetter,
    ProviderMail,
    ProviderReceiveFax,
    ProviderSendFax, ProviderUser, TwoFactorType,
} from '@enter/core';
import * as Sentry from '@sentry/react';
import * as d3 from 'd3';
import _ from 'lodash';
import { action, configure, observable, runInAction } from 'mobx';
import { ChargemasterStore } from './chargemaster.store';
import { ClaimStore } from './claim.store';
import { DateRangeStore } from './daterange.store';
import { DocumentStore } from './document.store';
import { EligibilityStore } from './eligibility.store';
import { GuarantorStore } from './guarantor.store';
import { LayoutStore } from './layout.store';
import { MessageStore } from './message.store';
import { MetricsStore } from './metrics.store';
import { PaymentStore } from './payment.store';
import { RootGenerator } from './root.generator';
import { SearchStore } from './search.store';
import { SettingsStore } from './settings.store';
import { TransactionStore } from './transaction.store';
import {PatientStore} from "./patient.store";
import {OverpaymentStore} from "./overpayment.store";
import { ExperimentsStore } from './experiments.store';
import { EvidenceStore } from './evidence.store';

configure({ enforceActions: 'observed' });

// feature types that require authentication
export type RequiredFeatures = 'DEV' | 'ADMIN';

export interface IFeatures {
    isAdmin: boolean;
    isLocal: boolean;
    isDev: boolean;
    isDemo: boolean;
    isProduction: boolean;
    isTest: boolean;
}

// NB: use dedicated interface for injected props so callers of this component
// do not need to specify 'rootStore' prop as required.
// See https://github.com/mobxjs/mobx-react/issues/256#issuecomment-459944336
export interface IInjectedRootStore {
    rootStore?: RootStore;
}
export interface IObservedRootStore {
    rootStore?: RootStore;
}

export enum IItemSortOrder {
    ASCENDING = 'ASCENDING',
    DESCENDING = 'DESCENDING',
}

export interface IItemListing<T> {
    items: T[];
    limit?: number;
    offset?: number;
    total: number;
}

export interface IItemListingRequest {
    page?: number;
    pageSize?: number;
    sort?: any[];
    order?: (keyof typeof IItemSortOrder)[];
    filters?: ICustomFilter[];
    institutional?: boolean;
}

export interface IItemDetail<T> {
    item?: T;
}

export interface IItemDetailRequest {
    id: string;
}

export type ICommunication = ProviderMail & ProviderLetter & ProviderSendFax & ProviderReceiveFax;
export enum ProviderCommunicationSortType {
    CREATED = 'CREATED',
}
export type ListProviderCommunicationsRequest = ListProviderMailRequest &
    ListProviderLettersRequest &
    ListProviderSendFaxesRequest &
    ListProviderReceiveFaxesRequest;

export class RootStore {
    @observable public lastCheck: Date = new Date(0);
    @observable public userId: string | undefined;
    @observable public userName: string | undefined;
    @observable public userHash: string | undefined;
    @observable public userEmail: string | undefined;
    @observable public userPhone: string | undefined;
    @observable public userTwoFactor: boolean = false;
    @observable public userTwoFactorType: TwoFactorType | undefined;
    @observable public companyName: string | undefined;
    @observable public roleId: string | undefined;
    @observable public account: MeInfoResponse | undefined;

    public rootGenerator: RootGenerator;
    public chargemasterStore: ChargemasterStore;
    public claimStore: ClaimStore;
    public claimEditorStore: ClaimEditorStore;
    public dateRangeStore: DateRangeStore;
    public guarantorStore: GuarantorStore;
    public paymentStore: PaymentStore;
    public overpaymentStore: OverpaymentStore;
    public documentStore: DocumentStore;
    public eligibilityStore: EligibilityStore;
    public messageStore: MessageStore;
    public metricsStore: MetricsStore;
    public searchStore: SearchStore;
    public settingsStore: SettingsStore;
    public transactionStore: TransactionStore;
    public layoutStore: LayoutStore;
    public patientStore: PatientStore;
    public experimentsStore: ExperimentsStore;
    public evidenceStore: EvidenceStore;

    private features: IFeatures;

    constructor() {
        this.features = {
            isAdmin: false,
            isDemo: false,
            isProduction: 'production' === process.env.NODE_ENV,
            isTest: 'test' === process.env.NODE_ENV,
            isDev: 'true' === process.env.REACT_APP_DEV_MODE,
            isLocal: 'true' === process.env.REACT_APP_LOCAL_ONLY,
        };
        this.features.isDemo = this.features.isLocal || this.features.isTest;

        this.rootGenerator = new RootGenerator(this);
        this.chargemasterStore = new ChargemasterStore(this.features, this.rootGenerator.chargemasterGenerator);
        this.claimStore = new ClaimStore(this.features, this.rootGenerator.claimGenerator);
        this.claimEditorStore = new ClaimEditorStore({
            onSubmit: async (data) => {
                // Provider-specific submission logic
                console.log('Provider app submitting claim:', data);
                // TODO: Implement actual submission logic
            },
        });
        this.dateRangeStore = new DateRangeStore();
        this.guarantorStore = new GuarantorStore(this.features, this.rootGenerator.guarantorGenerator);
        this.paymentStore = new PaymentStore(this.features, this.rootGenerator.paymentGenerator);
        this.overpaymentStore = new OverpaymentStore(this.features, this.rootGenerator.overpaymentGenerator);
        this.documentStore = new DocumentStore(this.features, this.rootGenerator.documentGenerator);
        this.eligibilityStore = new EligibilityStore(this.features, this.rootGenerator.eligibilityGenerator);
        this.messageStore = new MessageStore(this.features, this.rootGenerator.messageGenerator, this);
        this.metricsStore = new MetricsStore(this.features, this.rootGenerator.metricsGenerator);
        this.searchStore = new SearchStore(this.features, this.rootGenerator.claimGenerator);
        this.settingsStore = new SettingsStore(this.features, this.rootGenerator.settingsGenerator);
        this.transactionStore = new TransactionStore(this.features, this.rootGenerator.transactionGenerator);
        this.patientStore = new PatientStore(this.features, this.rootGenerator.patientGenerator);
        this.experimentsStore = new ExperimentsStore();
        this.evidenceStore = new EvidenceStore();
        this.layoutStore = new LayoutStore();
    }

    public isExpiredSession(): boolean {
        return !this.features.isDemo && !this.userId;
    }

    public isAdmin(): boolean {
        return this.features.isAdmin || this.features.isLocal;
    }

    public isDev = () => {
        return this.features.isDev || this.features.isDemo;
    };

    public isDemo(): boolean {
        return this.features.isDemo;
    }

    public isLocal(): boolean {
        return this.features.isLocal;
    }

    public isTest(): boolean {
        return this.features.isTest;
    }

    public isProduction(): boolean {
        return this.features.isProduction;
    }

    public getProviderUserFromAccount() : ProviderUser {

        const firstName: string = this.account?.personalInfo?.name?.split(' ')[0] || '';
        const lastName: string = this.account?.personalInfo?.name?.split(' ')[1] || '';

        return {
            userId: this.userId,
            first: firstName,
            last: lastName,
            email: this.userEmail,
            phone: this.userPhone,
            active: true,
            roles: this.account?.accountAccess?.map(access => ({
                accountId: access.accountId,
                roleId: access.roleId,
                name: access.name,
            })),
        };
    }

    @action
    public async checkSession(): Promise<void> {
        const tooSoon = d3.utcSecond.count(this.lastCheck, new Date()) <= 3;
        const notLoaded = this.userId === undefined;
        if (!tooSoon || notLoaded) {
            await this.loadUser();
        }
    }
    @action
    public async loadUser(): Promise<void> {
        // If not in LOCAL mode, check user to set demo mode
        if (!this.features.isLocal) {
            try {
                const me: MeInfoResponse = await CoreFetch<MeApi>(MeApi).getMeInfo();
                runInAction(() => {
                    this.account = me;
                    const personalInfo = me.personalInfo;
                    const accountAccess = _.find(this.account.accountAccess, { accountId: me.grantedAccountId });

                    this.userId = me.userId;
                    this.userName = _.get(personalInfo, 'name', '');
                    this.userHash = _.get(personalInfo, 'intercomHash', '');
                    this.userEmail = _.get(personalInfo, 'email', '');
                    this.userPhone = _.get(personalInfo, 'phone', '');
                    this.userTwoFactor = _.get(personalInfo, 'twoFactorEnabled', false);
                    this.userTwoFactorType = _.get(personalInfo, 'twoFactorType', undefined);
                    this.companyName = _.get(accountAccess, 'name', '');
                    this.roleId = _.get(accountAccess, 'roleId', '');
                    this.lastCheck = new Date();
                    this.features.isAdmin =
                        me.userId === 'f7c7b1be-9811-49e1-b0ed-9913159dba6f' ||
                        me.userId === '229aa9ac-0914-455f-8487-bc1b12356c54' ||
                        me.userId === '303f99ac-cefb-4a3e-9b45-45354c755531';
                    this.features.isDemo =
                        me.userId === 'ff016644-1d10-4e97-913a-a00aa4f75b3b' ||
                        me.grantedAccountId === '5c2ec5ea-6c41-487f-95b6-2a2fda2e0a7c';

                    Sentry.setUser({
                        id: me.userId,
                        email: _.get(personalInfo, 'email', ''),
                        username: _.get(personalInfo, 'name', ''),
                    });
                });
                // Note: unread total is now loaded by sidebar on mount or calculated from inbox messages
            } catch (err) {
                console.error(err);
                runInAction(() => {
                    this.userId = undefined;
                    this.userName = undefined;
                    this.userHash = undefined;
                    this.userEmail = undefined;
                    this.userPhone = undefined;
                    this.userTwoFactor = false;
                    this.companyName = undefined;
                    this.roleId = undefined;
                    this.features.isAdmin = false;
                    this.features.isDemo = false;
                });
            }
        } else {
            this.userName = 'Demo User';
        }
    }

    @action
    public async changeCompanyName(companyName: string): Promise<void> {
        if (companyName) {
            await CoreFetch<AccountApi>(AccountApi).accountPostBusinessNameChange({
                accountBusinessNameChangeRequest: {
                    businessName: companyName,
                },
            });
            runInAction(() => {
                this.companyName = companyName;
            });
        }
    }
}
